import React from 'react';
import { Button } from '@/components/ui/button';
import { ButtonSpinner } from '@/components/ui/button-spinner';
import { ArrowRight, Sparkles } from 'lucide-react';
import { CTA_GRADIENTS } from '@/lib/design-tokens';
import { TrackedLink } from './tracked-link';
import type { CtaClickedProperties } from '@/lib/posthog';
import type { LucideIcon } from 'lucide-react';

export interface MarketingCTAProps {
  /**
   * Main heading text
   */
  title: string;
  /**
   * Description text below the title
   */
  description: string;
  /**
   * Primary button text
   */
  primaryButtonText: string;
  /**
   * Primary button link URL
   */
  primaryButtonHref: string;
  /**
   * Optional: Secondary button text
   */
  secondaryButtonText?: string;
  /**
   * Optional: Secondary button link URL
   */
  secondaryButtonHref?: string;
  /**
   * Optional: Badge with icon and text displayed above the title
   */
  badge?: {
    icon: LucideIcon;
    text: string;
  };
  /**
   * Optional: Footer text displayed below buttons
   */
  footerText?: string;
  /**
   * Optional: Additional footer text (second line)
   */
  footerTextSecondary?: string;
  /**
   * Optional: Custom className for the section wrapper
   */
  className?: string;
  /**
   * Optional: ID for the heading (for aria-labelledby)
   */
  headingId?: string;
  /**
   * Optional: Custom onClick handler for primary button (if not using href)
   */
  primaryButtonOnClick?: () => void;
  /**
   * Optional: Whether primary button is disabled
   */
  primaryButtonDisabled?: boolean;
  /**
   * Optional: Loading state for primary button
   */
  primaryButtonLoading?: boolean;
  /**
   * Optional: When set, fires a `cta_clicked` event when the primary or
   * secondary link is clicked. Used to identify this CTA in the funnel
   * (e.g. 'final_cta').
   *
   * Only applies to the link-based render path. Client callers that supply
   * `primaryButtonOnClick` are expected to fire their own tracking inside
   * that handler.
   */
  trackingLocation?: string;
  /**
   * Optional: Page name recorded on the `cta_clicked` event. Required when
   * `trackingLocation` is set.
   */
  trackingPage?: string;
}

/**
 * Reusable marketing CTA banner component
 *
 * Provides consistent CTA styling with gradient background, decorative elements,
 * and flexible button configuration. Used across marketing pages for conversion.
 *
 * This component is intentionally NOT marked `'use client'` so it can be
 * rendered from Server Components (e.g. `force-static` marketing pages) with
 * their server-side children and icon props. Client-side click tracking is
 * delegated to the nested `TrackedLink` client component, which receives
 * only serializable props.
 *
 * @example
 * ```tsx
 * <MarketingCTA
 *   title="Ready to Get Started?"
 *   description="Start your free trial today"
 *   primaryButtonText="Get Started Free"
 *   primaryButtonHref="/login"
 *   secondaryButtonText="View Features"
 *   secondaryButtonHref="/features"
 *   badge={{ icon: Sparkles, text: "New Feature" }}
 *   footerText="14-day free trial • Cancel anytime"
 *   trackingLocation="final_cta"
 *   trackingPage="landing"
 * />
 * ```
 *
 * @see {@link TrackedLink} - Client wrapper that fires the `cta_clicked` event
 * @see {@link CTA_GRADIENTS} - Gradient configuration from design tokens
 */
export function MarketingCTA({
  title,
  description,
  primaryButtonText,
  primaryButtonHref,
  secondaryButtonText,
  secondaryButtonHref,
  badge,
  footerText,
  footerTextSecondary,
  className = '',
  headingId,
  primaryButtonOnClick,
  primaryButtonDisabled = false,
  primaryButtonLoading = false,
  trackingLocation,
  trackingPage,
}: MarketingCTAProps): React.ReactElement {
  const hasSecondaryButton = secondaryButtonText && secondaryButtonHref;
  const BadgeIcon = badge?.icon || Sparkles;

  const primaryTracking: CtaClickedProperties | undefined =
    trackingLocation && trackingPage
      ? {
          location: trackingLocation,
          destination: primaryButtonHref,
          page: trackingPage,
          label: primaryButtonText,
        }
      : undefined;

  const secondaryTracking: CtaClickedProperties | undefined =
    trackingLocation && trackingPage && secondaryButtonHref
      ? {
          location: `${trackingLocation}_secondary`,
          destination: secondaryButtonHref,
          page: trackingPage,
          label: secondaryButtonText,
        }
      : undefined;

  return (
    <section aria-labelledby={headingId} className={`relative mb-16 ${className}`}>
      {/* Blur background */}
      <div className={`absolute inset-0 ${CTA_GRADIENTS.blur} rounded-3xl blur-2xl opacity-50`} />
      {/* Main gradient container */}
      <div
        className={`relative ${CTA_GRADIENTS.base} rounded-3xl p-12 md:p-16 text-white ${CTA_GRADIENTS.shadow} overflow-hidden`}
      >
        {/* Animated background pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px] opacity-50" />
        {/* Decorative gradient blobs */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />

        <div className="relative z-10 text-center max-w-3xl mx-auto">
          {/* Badge */}
          {badge && (
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
              <BadgeIcon className="h-4 w-4 text-white" />
              <span className="text-sm font-semibold">{badge.text}</span>
            </div>
          )}

          {/* Title */}
          <h2 id={headingId} className="text-4xl md:text-5xl font-bold mb-6">
            {title}
          </h2>

          {/* Description */}
          <p className="text-xl md:text-2xl mb-10 opacity-95 leading-relaxed">{description}</p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {/* Primary Button */}
            {primaryButtonOnClick ? (
              <Button
                onClick={primaryButtonOnClick}
                disabled={primaryButtonDisabled || primaryButtonLoading}
                size="lg"
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300 font-semibold px-8 py-6 text-lg group"
              >
                {primaryButtonLoading ? (
                  <>
                    <ButtonSpinner className="border-gray-900" />
                    Processing...
                  </>
                ) : (
                  <>
                    {primaryButtonText}
                    <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                asChild
                size="lg"
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300 font-semibold px-8 py-6 text-lg group"
              >
                <TrackedLink
                  href={primaryButtonHref}
                  className="flex items-center gap-2"
                  tracking={primaryTracking}
                >
                  {primaryButtonText}
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </TrackedLink>
              </Button>
            )}

            {/* Secondary Button */}
            {hasSecondaryButton && (
              <Button
                asChild
                variant="outline"
                size="lg"
                className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/50 transition-all duration-300 font-semibold px-8 py-6 text-lg"
              >
                <TrackedLink href={secondaryButtonHref!} tracking={secondaryTracking}>
                  {secondaryButtonText}
                </TrackedLink>
              </Button>
            )}
          </div>

          {/* Footer Text */}
          {footerText && <p className="mt-8 text-sm opacity-80">{footerText}</p>}
          {footerTextSecondary && <p className="mt-4 text-sm opacity-70">{footerTextSecondary}</p>}
        </div>
      </div>
    </section>
  );
}
